import {
  type Note,
  type Patient,
  type ReportEntry,
  type ReportPatientGroup,
  type ReportResponse,
  type SendNoteResponse,
  formatEmails,
  parseEmails,
} from '@ergo/shared';
import { formatShortDate, today } from './time';

/**
 * La clôture d'un mois, vue comme une suite de petites étapes plutôt que comme un
 * tableau de chiffres (docs/redesign-plus-douce.md, §3). Le modèle est calculé ici,
 * à part de l'écran : c'est lui qui décide de l'anneau, de la phrase d'accroche, de
 * la prochaine étape et de l'ordre de la liste repliée — trois lectures d'une seule
 * vérité, qui ne peuvent donc pas se contredire.
 *
 * Compter les étapes ainsi (une par séance à pointer, une pour émettre, une pour
 * envoyer) fait du pointage la première étape de la facture (principe 5) : l'anneau
 * avance déjà quand elle pointe, et pas seulement à l'émission.
 *
 * Toute facture part par email : il n'y a pas de remise en main propre. Une famille
 * sans adresse, ou sans consentement, retient donc le mois comme une séance à
 * pointer — tant que sa fiche n'est pas corrigée, sa facture ne peut pas partir.
 */

/** Là où en est la facture d'un patient — l'ordre des étapes, dans l'ordre. */
export type EtatPatient = 'pointage' | 'email' | 'emettre' | 'envoyer' | 'fini';

export interface PatientCloture {
  patientId: number;
  name: string;
  /** Le prénom, tel que les toasts et les phrases d'aide s'adressent à la famille. */
  firstName: string;
  group: ReportPatientGroup;
  /** Les factures du mois pour ce patient, la plus récente d'abord (ordre du serveur). */
  notes: Note[];
  lastNote: Note | null;
  /** La première facture émise qui n'est pas partie : celle que « Envoyer » enverrait. */
  unsentNote: Note | null;
  /** Une adresse, et le consentement qui va avec — la même lecture que le serveur. */
  canEmail: boolean;
  /** Combien d'adresses recevront chacune leur copie ; 0 quand `canEmail` est faux. */
  recipients: number;
  /** Les deux raisons distinctes de ne pas être joignable : l'avertissement dit laquelle. */
  hasAddress: boolean;
  consent: boolean;
  /** Ce qui reste à mettre sur une facture : la clôture émet tant que c'est plus que 0. */
  needsIssue: boolean;
  /** Rien de facturable, aucune facture : la clôture passe son chemin. */
  nothingToBill: boolean;
  /** Les séances du mois encore à pointer, la plus ancienne d'abord. */
  unlogged: ReportEntry[];
  state: EtatPatient;
  steps: number;
  done: number;
}

export interface Cloture {
  patients: PatientCloture[];
  /** Σ des étapes du mois, et celles qui sont faites : les deux chiffres de l'anneau. */
  steps: number;
  done: number;
  remaining: number;
  allDone: boolean;
  /** Le premier patient dont la facture n'est pas terminée : la carte « Prochaine étape ». */
  next: PatientCloture | null;
  /** Tous les autres, dans l'ordre du rapport : la liste repliée dessous. */
  others: PatientCloture[];
  /** Factures parties, et patients qu'il reste à finir — la légende de l'anneau. */
  sentNotes: number;
  pendingNotes: number;
  /** Séances du mois encore à pointer, tous patients confondus. */
  unloggedCount: number;
}

export function planCloture(
  report: ReportResponse,
  notes: Note[],
  patients: Patient[],
): Cloture {
  const byId = new Map(patients.map((patient) => [patient.id, patient]));

  const plans = report.patients.map((group) =>
    planPatient(
      group,
      notes.filter((note) => note.patientId === group.patientId),
      byId.get(group.patientId),
    ),
  );

  const steps = plans.reduce((total, plan) => total + plan.steps, 0);
  const done = plans.reduce((total, plan) => total + plan.done, 0);
  const next = plans.find((plan) => plan.state !== 'fini') ?? null;
  const doneNotes = plans.filter((plan) => plan.state === 'fini').length;

  return {
    patients: plans,
    steps,
    done,
    remaining: steps - done,
    allDone: next === null,
    next,
    others: plans.filter((plan) => plan !== next),
    sentNotes: doneNotes,
    pendingNotes: plans.length - doneNotes,
    unloggedCount: plans.reduce((total, plan) => total + plan.unlogged.length, 0),
  };
}

function planPatient(
  group: ReportPatientGroup,
  notes: Note[],
  patient: Patient | undefined,
): PatientCloture {
  const addresses = parseEmails(patient?.email).length;
  const consent = patient?.emailConsent ?? false;
  const recipients = consent ? addresses : 0;
  const canEmail = recipients > 0;
  const unlogged = group.entries.filter((entry) => entry.status === 'planned');
  // Un mois où tout a été annulé — ou entièrement offert — ne se facture pas :
  // l'émission et l'envoi n'y sont pas des étapes en attente, ils sont sans objet.
  // Les compter comme faites garde l'anneau honnête : il ne réclame jamais un geste
  // que le serveur refuserait.
  const nothingToBill =
    group.uninvoicedBillableCount === 0 && unlogged.length === 0 && notes.length === 0;
  const issued = notes.length > 0 && group.uninvoicedBillableCount === 0;
  const unsentNote = notes.filter((note) => note.sentTo === null).at(-1) ?? null;
  const sent = notes.length > 0 && unsentNote === null;

  const pointed = group.entries.length - unlogged.length;
  const emitted = issued || nothingToBill;
  const delivered = sent || nothingToBill;

  return {
    patientId: group.patientId,
    name: group.patientName,
    firstName: firstNameOf(group.patientName),
    group,
    notes,
    lastNote: notes[0] ?? null,
    unsentNote,
    canEmail,
    recipients,
    hasAddress: addresses > 0,
    consent,
    needsIssue: group.uninvoicedBillableCount > 0,
    nothingToBill,
    unlogged,
    state: stateOf({ unlogged, emitted, delivered, canEmail }),
    steps: group.entries.length + 2,
    done: pointed + (emitted ? 1 : 0) + (delivered ? 1 : 0),
  };
}

function stateOf(patient: {
  unlogged: ReportEntry[];
  emitted: boolean;
  delivered: boolean;
  canEmail: boolean;
}): EtatPatient {
  // Tant qu'une séance n'est pas pointée, la facture ne peut pas être émise : l'ordre
  // des états est celui des gestes, et il ne se saute pas.
  if (patient.unlogged.length > 0) return 'pointage';
  if (!patient.delivered && !patient.canEmail) return 'email';
  if (!patient.emitted) return 'emettre';
  if (!patient.delivered) return 'envoyer';
  return 'fini';
}

/** « Envoyée le 14/09 à a@x.fr, b@y.fr » ; null pour une facture jamais partie. */
export function formatSent(note: { sentTo: string | null; sentAt: string | null }): string | null {
  if (note.sentTo === null || note.sentAt === null) return null;
  return `Envoyée le ${formatShortDate(today(new Date(note.sentAt)))} à ${note.sentTo}`;
}

/** « Léa Marchand » → « Léa » : ce que les toasts et les phrases d'aide disent. */
export function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/*
 * « Clore le mois » : la liste à vérifier, puis un seul geste qui émet et envoie tout
 * ce qui manque. Ce qui suit reste une lecture de `planCloture` — l'anneau, la liste
 * et le plan du bouton ne peuvent pas se contredire.
 */

/**
 * Ce qu'une ligne de la vérification dit sous le nom. `bloquant` retient la clôture,
 * `etat` raconte ce qui est déjà fait.
 */
export type NoticeKind = 'bloquant' | 'etat';

export interface Notice {
  kind: NoticeKind;
  text: string;
  /** Ce blocage se lève en corrigeant la fiche : le lien y mène. */
  editPatient?: boolean;
}

/**
 * Un pas de la clôture, une requête. `noteId: null` vise la facture que l'émission
 * juste avant aura créée : son numéro n'existe pas encore quand le plan se fait.
 */
export type CloseStep =
  | { kind: 'issue'; patientId: number; name: string }
  | { kind: 'send'; patientId: number; name: string; noteId: number | null };

export interface VerificationRow {
  patient: PatientCloture;
  notices: Notice[];
  /** Ce que « Clore le mois » fera pour cette famille, dans l'ordre. */
  steps: CloseStep[];
}

export interface Verification {
  rows: VerificationRow[];
  /** Séances à pointer, et familles à facturer sans email : tant qu'il en reste, le bouton attend. */
  blocking: number;
  unlogged: number;
  withoutEmail: number;
  steps: CloseStep[];
  toIssue: number;
  toSend: number;
  /** Le plan mené au bout, le mois est bouclé — faux quand l'email manque côté Réglages. */
  closesMonth: boolean;
  emailConfigured: boolean;
}

export function verificationOf(
  cloture: Cloture,
  options: { emailConfigured: boolean },
): Verification {
  const rows = cloture.patients.map((patient) => ({
    patient,
    notices: noticesOf(patient),
    steps: stepsOf(patient, options.emailConfigured),
  }));
  const steps = rows.flatMap((row) => row.steps);
  const unlogged = cloture.unloggedCount;
  const withoutEmail = cloture.patients.filter(withoutEmailOf).length;
  const blocking = unlogged + withoutEmail;

  return {
    rows,
    blocking,
    unlogged,
    withoutEmail,
    steps,
    toIssue: steps.filter((step) => step.kind === 'issue').length,
    toSend: steps.filter((step) => step.kind === 'send').length,
    // Sans SMTP, le plan émet mais n'envoie rien : le mois ne se boucle pas.
    closesMonth: blocking === 0 && options.emailConfigured,
    emailConfigured: options.emailConfigured,
  };
}

/** Une famille qui a quelque chose à facturer et à qui la facture ne peut pas partir. */
function withoutEmailOf(patient: PatientCloture): boolean {
  return !patient.nothingToBill && !patient.canEmail;
}

/** « Encore 2 séances à pointer · 1 famille sans email » : ce qui retient le bouton. */
export function blockingReason(verification: Verification): string {
  const { unlogged, withoutEmail } = verification;
  const parts: string[] = [];
  if (unlogged > 0) {
    parts.push(
      unlogged === 1 ? 'Encore une séance à pointer' : `Encore ${unlogged} séances à pointer`,
    );
  }
  if (withoutEmail > 0) {
    parts.push(`${withoutEmail} famille${s(withoutEmail)} sans email`);
  }
  return parts.join(' · ');
}

function noticesOf(patient: PatientCloture): Notice[] {
  const notices: Notice[] = [];
  const pending = patient.unlogged.length;
  if (pending > 0) {
    notices.push({
      kind: 'bloquant',
      text: pending === 1 ? 'Une séance à pointer' : `${pending} séances à pointer`,
    });
  }

  if (patient.nothingToBill) {
    notices.push({ kind: 'etat', text: 'Rien à facturer' });
    return notices;
  }

  if (!patient.canEmail) {
    notices.push({
      kind: 'bloquant',
      text: patient.hasAddress ? 'N’accepte pas l’envoi par email' : 'Pas d’adresse email',
      editPatient: true,
    });
  }

  // La plus ancienne d'abord : l'ordre dans lequel elles ont été faites.
  for (const note of [...patient.notes].reverse()) {
    notices.push({ kind: 'etat', text: noteStateOf(note) });
  }
  return notices;
}

function noteStateOf(note: Note): string {
  if (note.sentAt !== null && note.sentTo !== null) {
    return `Facture ${note.number} envoyée le ${formatShortDate(today(new Date(note.sentAt)))}`;
  }
  return `Facture ${note.number} émise, pas encore envoyée`;
}

function stepsOf(patient: PatientCloture, emailConfigured: boolean): CloseStep[] {
  // Une séance à pointer retient toute la famille : émettre maintenant laisserait la
  // séance hors de la facture du mois. Sans email, la facture n'aurait nulle part où aller.
  if (patient.unlogged.length > 0 || !patient.canEmail) return [];

  const { patientId, name } = patient;
  const steps: CloseStep[] = [];
  if (patient.needsIssue) steps.push({ kind: 'issue', patientId, name });
  if (!emailConfigured) return steps;

  const unsent = patient.notes.filter((note) => note.sentTo === null).reverse();
  for (const note of unsent) steps.push({ kind: 'send', patientId, name, noteId: note.id });
  if (patient.needsIssue) steps.push({ kind: 'send', patientId, name, noteId: null });
  return steps;
}

/** Ce qui manque et n'a pas abouti, avec de quoi le dire et de quoi le refaire. */
export interface CloseFailure {
  step: CloseStep;
  message: string;
  /** Un envoi partiel : les adresses qu'il n'a pas atteintes. Vide sinon. */
  unreached: string[];
}

export interface CloseOutcome {
  issued: number;
  sent: number;
  failures: CloseFailure[];
  /** patientId → la facture émise pendant le passage : le réessai ne la réémet pas. */
  issuedNotes: Map<number, number>;
  /** Les factures parties en entier pendant le passage : le réessai ne les renvoie pas. */
  sentNotes: Set<number>;
}

export interface CloseProgress {
  done: number;
  total: number;
  /** Le pas en cours ; null une fois le passage fini. */
  current: CloseStep | null;
}

export interface CloseApi {
  issue: (patientId: number) => Promise<Note>;
  send: (noteId: number) => Promise<SendNoteResponse>;
}

/**
 * Le passage : un pas après l'autre, une requête à la fois. Une erreur ne l'arrête
 * jamais — elle est notée et l'on passe à la suite ; seul l'envoi d'une facture dont
 * l'émission vient d'échouer est sauté, puisqu'il n'a rien à envoyer.
 */
export async function runClose(
  steps: CloseStep[],
  api: CloseApi,
  onProgress: (progress: CloseProgress) => void = () => {},
): Promise<CloseOutcome> {
  const outcome: CloseOutcome = {
    issued: 0,
    sent: 0,
    failures: [],
    issuedNotes: new Map(),
    sentNotes: new Set(),
  };
  const total = steps.length;

  for (const [index, step] of steps.entries()) {
    onProgress({ done: index, total, current: step });
    if (step.kind === 'issue') {
      try {
        const note = await api.issue(step.patientId);
        outcome.issued += 1;
        outcome.issuedNotes.set(step.patientId, note.id);
      } catch (cause) {
        outcome.failures.push({ step, message: messageOf(cause), unreached: [] });
      }
      continue;
    }

    const noteId = step.noteId ?? outcome.issuedNotes.get(step.patientId);
    if (noteId === undefined) continue;
    const resolved = { ...step, noteId };
    try {
      const { failed } = await api.send(noteId);
      if (failed.length === 0) {
        outcome.sent += 1;
        outcome.sentNotes.add(noteId);
      } else {
        outcome.failures.push({
          step: resolved,
          message: `Pas reçue par ${formatEmails(failed)}`,
          unreached: failed,
        });
      }
    } catch (cause) {
      outcome.failures.push({ step: resolved, message: messageOf(cause), unreached: [] });
    }
  }

  onProgress({ done: total, total, current: null });
  return outcome;
}

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : 'Erreur inattendue';
}

/**
 * Réessayer ne refait que ce qui a échoué. Le plan repart des données fraîches —
 * elle a pu corriger une fiche entre-temps — mais ce que le passage a déjà fait n'y
 * revient pas, même si le rechargement n'a pas encore atterri : une facture émise ne
 * se réémet pas, une facture partie ne se renvoie pas.
 */
export function retryPlan(verification: Verification, previous: CloseOutcome): CloseStep[] {
  const failedPatients = new Set(previous.failures.map((failure) => failure.step.patientId));
  const steps: CloseStep[] = [];
  const seen = new Set<number>();

  for (const step of verification.steps) {
    if (!failedPatients.has(step.patientId)) continue;
    if (step.kind === 'issue') {
      if (!previous.issuedNotes.has(step.patientId)) steps.push(step);
      continue;
    }
    const noteId = step.noteId ?? previous.issuedNotes.get(step.patientId) ?? null;
    if (noteId !== null && (previous.sentNotes.has(noteId) || seen.has(noteId))) continue;
    if (noteId !== null) seen.add(noteId);
    steps.push({ ...step, noteId });
  }

  // Un envoi partiel a marqué la facture comme partie : le plan frais ne la voit plus
  // en attente, il faut la redemander nommément.
  for (const failure of previous.failures) {
    const { step } = failure;
    if (step.kind !== 'send' || step.noteId === null || seen.has(step.noteId)) continue;
    if (failure.unreached.length === 0) continue;
    seen.add(step.noteId);
    steps.push(step);
  }
  return steps;
}

/** Deux passages lus comme un seul : les comptes s'ajoutent, les échecs sont les derniers. */
export function mergeOutcomes(first: CloseOutcome, second: CloseOutcome): CloseOutcome {
  return {
    issued: first.issued + second.issued,
    sent: first.sent + second.sent,
    failures: second.failures,
    issuedNotes: new Map([...first.issuedNotes, ...second.issuedNotes]),
    sentNotes: new Set([...first.sentNotes, ...second.sentNotes]),
  };
}

/** La phrase sous le bouton : ce que le geste va faire, en clair. */
export function closeHint(verification: Verification): string {
  const { toIssue, toSend } = verification;
  const parts: string[] = [];
  if (toIssue > 0) {
    parts.push(
      toIssue === 1 ? 'Émet la facture qui manque' : `Émet les ${toIssue} factures qui manquent`,
    );
  }
  if (toSend > 0) {
    parts.push(
      toIssue > 0
        ? 'envoie celles qui ont une adresse email'
        : toSend === 1
          ? 'Envoie la facture qui attend son email'
          : `Envoie les ${toSend} factures qui attendent leur email`,
    );
  }
  let hint = parts.length > 0 ? `${parts.join(' et ')}.` : '';
  if (!verification.emailConfigured) {
    hint = `${hint} L’envoi par email n’est pas configuré (Réglages).`.trim();
  }
  return hint;
}

/** « 8 factures émises · 8 envoyées » */
export function outcomeSummary(outcome: CloseOutcome): string {
  if (outcome.issued === 0 && outcome.sent === 0) return 'Rien n’est parti cette fois';
  const parts: string[] = [];
  if (outcome.issued > 0) {
    parts.push(`${outcome.issued} facture${s(outcome.issued)} émise${s(outcome.issued)}`);
  }
  if (outcome.sent > 0) {
    parts.push(
      `${outcome.sent} ${outcome.issued > 0 ? '' : `facture${s(outcome.sent)} `}envoyée${s(
        outcome.sent,
      )}`,
    );
  }
  return parts.join(' · ');
}

/** « 1 envoi a échoué : Léa Marchand » — une phrase par sorte de pas. */
export function failureLines(failures: CloseFailure[]): string[] {
  const lines: string[] = [];
  const issues = failures.filter((failure) => failure.step.kind === 'issue');
  const sends = failures.filter((failure) => failure.step.kind === 'send');
  if (issues.length > 0) {
    lines.push(
      `${issues.length} émission${s(issues.length)} ${issues.length > 1 ? 'ont' : 'a'} échoué : ${namesOf(issues)}`,
    );
  }
  if (sends.length > 0) {
    lines.push(
      `${sends.length} envoi${s(sends.length)} ${sends.length > 1 ? 'ont' : 'a'} échoué : ${namesOf(sends)}`,
    );
  }
  return lines;
}

function namesOf(failures: CloseFailure[]): string {
  return failures
    .map((failure) =>
      failure.unreached.length > 0
        ? `${failure.step.name} (${formatEmails(failure.unreached)})`
        : failure.step.name,
    )
    .join(', ');
}

/** Le toast de fin. */
export function bouclageToast(month: string): string {
  return `${month} est bouclé`;
}

function s(count: number): string {
  return count > 1 ? 's' : '';
}
